/**
 * MainHeader — top bar with KPMG brand, Clara app name, configurable header
 * actions, and profile/settings fly-in.
 *
 * Notification is handled by AppShell because NotificationPanel already exists.
 * Feedback is handled by AppShell because it opens a modal.
 * Profile and settings reuse the existing user-profile-menu fly-in.
 * CSS in `src/styles/shell.css` (`.main-header` and child BEM selectors).
*/

import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar/Avatar";
import { DialogBox } from "kci-design-lib";
import { useMsalAuth } from "../../auth/useMsalAuth";
import type {
  MainHeaderAction,
  MainHeaderActionId,
} from "../../services/mainHeaderService";
import { NotificationIcon } from '../notifications/NotificationIcon';
import { useNotifications, useUserProfile, useApplicationDisclaimer, useApplicationSettings } from '../../hooks';
import { usePreferencesContext } from '../../context/PreferencesContext';
import { useT } from '../../i18n/I18nProvider';
import { renderDisclaimerContent } from '../../components/Disclaimer/DisclaimerContent';
import onboardingTourService from '../../services/onboardingTourService';
 
// 15 languages matching prototype i18n.js
const LANGUAGES = [
  { code: "en", locale: "en-US", name: "English" },
  { code: "es", locale: "es-ES", name: "Español" },
  { code: "fr", locale: "fr-FR", name: "Français" },
  { code: "de", locale: "de-DE", name: "Deutsch" },
  { code: "pt", locale: "pt-BR", name: "Português" },
  { code: "ja", locale: "ja-JP", name: "日本語" },
  { code: "zh", locale: "zh-CN", name: "中文 (简体)" },
  { code: "ko", locale: "ko-KR", name: "한국어" },
  { code: "it", locale: "it-IT", name: "Italiano" },
  { code: "nl", locale: "nl-NL", name: "Nederlands" },
  { code: "ar", locale: "ar-SA", name: "العربية" },
  { code: "hi", locale: "hi-IN", name: "हिन्दी" },
  { code: "th", locale: "th-TH", name: "ไทย" },
  { code: "pl", locale: "pl-PL", name: "Polski" },
  { code: "tr", locale: "tr-TR", name: "Türkçe" },
] as const;

interface MainHeaderProps {
  actions: MainHeaderAction[];
  onToggleAi: () => void;
  onToggleNotifications: () => void;
  onCloseNotifications: () => void;
  onOpenFeedback: () => void;
  onStartWelcomeTutorial?: () => void;
  aiPanelOpen: boolean;
  notifPanelOpen: boolean;
}

type HeaderFlyinMode = Extract<MainHeaderActionId, "profile" | "settings"> | null;

function formatFallbackUserName(username?: string): string {
  const localPart = username?.split("@")[0]?.trim() ?? "";

  if (!localPart) {
    return "";
  }

  const segments = localPart
    .split(/[._-]+/)
    .map((segment) => segment.replace(/\d+/g, "").trim())
    .filter(Boolean);

  if (segments.length === 0) {
    return localPart;
  }

  return segments
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function formatUserInitials(displayName: string, username?: string): string {
  const nameParts = displayName.split(/\s+/).filter(Boolean);

  if (nameParts.length >= 2) {
    return `${nameParts[0].charAt(0)}${nameParts[
      nameParts.length - 1
    ].charAt(0)}`.toUpperCase();
  }

  const fallbackSource = (username?.split("@")[0] ?? displayName).replace(
    /[^a-zA-Z0-9]/g,
    "",
  );

  if (fallbackSource.length >= 2) {
    return `${fallbackSource.charAt(0)}${fallbackSource.charAt(
      fallbackSource.length - 1,
    )}`.toUpperCase();
  }

  return fallbackSource.charAt(0).toUpperCase() || "U";
}

// KPMG brand wordmark path, exactly as in the prototype.
const KPMG_LOGO_PATH =
  "M56.5556 18.2682H53.5932L54.0819 16.215H48.1472L47.6585 18.2682H44.7907V17.8466C44.835 17.6148 44.8732 17.3723 44.9295 17.113C45.4504 14.8996 46.838 12.7115 49.2352 12.7115C50.1845 12.7115 51.1277 13.0909 50.9989 14.4675H54.5284C54.6672 13.8245 54.9004 12.7325 54.2348 11.727C53.4826 10.635 51.9663 10.1966 50.0014 10.1966C48.6037 10.1966 46.5585 10.4306 44.7907 11.6406V0.4722H56.5515V18.2682H56.5556ZM49.6153 21.2764C48.9436 21.4029 48.2739 21.4893 47.6384 21.4893C45.9431 21.4893 44.7646 20.663 44.7385 18.7362H50.2166L49.6153 21.2764ZM42.838 11.1115L42.832 13.6622C42.0114 14.849 41.5026 16.1455 41.2653 17.1889C41.1748 17.5536 41.1125 17.9099 41.0783 18.2682H39.3508L40.8269 10.9217L35.8555 10.9175L31.405 18.2704H31.0812V0.4722H42.836V11.1094L42.838 11.1115ZM36.2718 18.2682H34.5041L37.1808 13.854L36.2718 18.2682ZM29.1345 10.9049H26.1923L24.1732 18.2704H21.054C22.6447 17.6548 23.6121 16.4764 23.9097 14.731C24.1571 13.3734 24.0384 12.4796 23.5115 11.8113C22.7192 10.8163 21.1324 10.9007 19.7206 10.9007C19.4733 10.9007 17.3878 10.9049 17.3878 10.9049V0.4722H29.1345V10.9028V10.9049ZM27.2521 18.2682L28.4909 13.6622L28.5352 18.2682H27.2541H27.2521ZM18.697 16.5165H18.693C18.5783 16.5228 18.4637 16.5333 18.333 16.5333C18.1621 16.5333 18.0273 16.5397 17.9006 16.5397L17.1485 16.5333L17.4964 15.1842L17.6613 14.5138L18.0555 12.9455C18.2304 12.9455 18.4014 12.9391 18.5663 12.9391H19.1495C20.143 12.9391 20.7724 12.9982 21.0017 13.3333C21.1767 13.5842 21.1545 14.0206 20.9474 14.6804C20.5934 15.8208 20.143 16.3984 18.697 16.5144M15.429 11.5563L15.244 12.195L13.5627 18.0617L13.4963 18.2682H7.96186L7.52948 17.3133L13.4963 10.9217H9.66323L4.99753 16.1897L6.50986 10.9217H3.68228V0.4722H15.429V11.5563ZM44.3463 0V11.9758C43.9481 12.3067 43.6082 12.6609 43.2925 13.0403V0H30.6388V10.9049H29.591V0H16.9353V10.9217H15.8875V0H3.22979V12.448L0 23.7344H2.83964L4.2655 18.7362H4.67576L7.03073 23.7344H10.4616L8.17705 18.7362H13.3636L11.9237 23.7344H15.0268L16.4466 18.751H17.1284V18.7362H24.0384L22.6689 23.7196H25.7981L27.1254 18.7362H28.5332L28.5714 23.7196H31.1959L34.2084 18.7362H36.1813L35.1637 23.7196H38.2447L39.2442 18.7362H41.026C40.9556 20.2814 41.3317 21.6833 42.3091 22.6298C43.5037 23.7808 45.3357 24 46.6932 24C48.5494 24 50.4761 23.7217 52.4147 23.2622L53.4826 18.7362H57V0H44.3463Z";

export const MainHeader = React.memo(function MainHeader({
  actions,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onToggleAi: _onToggleAi,
  onToggleNotifications,
  onCloseNotifications,
  onOpenFeedback,
  onStartWelcomeTutorial,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  aiPanelOpen: _aiPanelOpen,
  notifPanelOpen,
}: MainHeaderProps) {
  const t = useT();
  // ── User profile ──────────────────────────────────────────────────────
  const { account, resetTokenTimeout } = useMsalAuth();
  const isProfileEnabled = import.meta.env.VITE_enableUserProfileIcon !== 'false';
  const { profile, isLoading: profileLoading } = useUserProfile({ enabled: isProfileEnabled });
  const userName =
    profile?.displayName?.trim() ||
    account?.name?.trim() ||
    formatFallbackUserName(account?.username) ||
    "User";
  const userEmail = profile?.email?.trim() || account?.username || '';
  const userInitials = formatUserInitials(userName, userEmail);
  const avatarUrl = profile?.avatarUrl;
  const { unreadCount } = useNotifications();
  const { applicationSettings } = useApplicationSettings();
  // ── Alert pills (hidden by default per prototype Session 29) ──────────
  const [alertAVisible, setAlertAVisible] = useState(false);
  const [alertSVisible, setAlertSVisible] = useState(false);

  // ── Profile menu ───────────────────────────────────────────────────────
  const [activeFlyinMode, setActiveFlyinMode] =
    useState<HeaderFlyinMode>(null);

  const headerFlyinOpen = activeFlyinMode !== null;

  // Header is expanded only when settings, profile, or notifications are open.
  const isCollapsed = activeFlyinMode === null && !notifPanelOpen;
  const [langExpanded, setLangExpanded] = useState(false);
  const [langSearchQuery, setLangSearchQuery] = useState('');
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  // User preferences from context with backend sync
  const { 
    preferences, 
    isLoading: prefsLoading, 
    updateTheme,
    updateLanguage
  } = usePreferencesContext();

  // (profile is already resolved above from the feature-gated hook)

  // Disclaimer acknowledgment (GET content, then POST acknowledgment)
  const { 
    disclaimerContent,
    isLoading: disclaimerLoading,
    isAcknowledging, 
    error: disclaimerError, 
    acknowledge: acknowledgeDisclaimer 
  } = useApplicationDisclaimer();

  // Computed values from preferences
  const currentTheme = preferences.theme || 'light';
  const selectedLang = preferences.language || 'en';

  const settingsTitle = t('settings.title');
  const languageLabel = t('header.language');
  const welcomeTutorialLabel = t('header.welcomeTutorial');
  const systemCardLabel = t('header.systemCard');
  const guidesLabel = t('header.guides');
  const disclaimerLabel = t('profile.disclaimer');
  const lightModeLabel = t('header.lightMode');
  const darkModeLabel = t('header.darkMode');
  const closeLabel = t('common.close');
  const acceptLabel = t('disclaimer.accept');
  const profileLoadingLabel = t('profile.loading');
  const profileTitle = t('profile.title');
  const viewWorkspaceLabel = t('header.viewWorkspace');
  const searchLanguagePlaceholder = t('header.searchLanguagePlaceholder');
  const noLanguageResultsLabel = t('header.noLanguageResults');
  const guidesUrl = applicationSettings?.userGuideLinks?.[0] || '';
  const systemCardUrl = applicationSettings?.systemCardLinks?.[0] || '';

  const getLanguageDisplayName = (code: string, fallback: string) => {
    const key = `languages.${code}`;
    const value = t(key);
    return value === key ? fallback : value;
  };

  const selectedLangName =
    (() => {
      const selected = LANGUAGES.find((lang) => lang.locale === selectedLang || lang.code === selectedLang);
      if (!selected) return "English";
      return getLanguageDisplayName(selected.code, selected.name);
    })() ??
    "English";

  const normalizedLangSearchQuery = langSearchQuery.trim().toLowerCase();
  const filteredLanguages = LANGUAGES.filter((lang) => {
    if (!normalizedLangSearchQuery) {
      return true;
    }

    const displayName = getLanguageDisplayName(lang.code, lang.name).toLowerCase();
    return (
      displayName.includes(normalizedLangSearchQuery) ||
      lang.locale.toLowerCase().includes(normalizedLangSearchQuery) ||
      lang.code.toLowerCase().includes(normalizedLangSearchQuery)
    );
  });

  const handleThemeToggle = () => {
    console.log('[MainHeader.handleThemeToggle] Called! Current theme:', currentTheme);
    const nextTheme = currentTheme === 'light' ? 'dark' : 'light';
    console.log('[MainHeader.handleThemeToggle] Calling updateTheme with:', nextTheme);
    updateTheme(nextTheme);
  };

  const handleSystemCardOpen = () => {
    if (systemCardUrl) {
      window.open(systemCardUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleGuidesOpen = () => {
    if (guidesUrl) {
      window.open(guidesUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleStartWelcomeTutorialClick = () => {
    // Detect the create-agent screen by the starter-prompts section which is
    // always rendered in the create-new-agent component template.
    const isCreateAgentScreen =
      !!document.querySelector('[data-tour-id="create-agent-starter-prompts-section"]') ||
      !!document.querySelector('[data-tour-id="create-agent-skill-section"]');
    // Detect the activity dashboard: either the footer "New Agent" button (when data exists)
    // or the zero-state button / create-task button are present in the DOM.
    const isActivityDashboardScreen =
      !!document.querySelector('[data-tour-id="new-agent"]') ||
      !!document.querySelector('[data-tour-id="activity-create-task-btn"]');
    const isNavHeaderContextActive = !!document.querySelector(
      '[data-tour-id="workspace-tools-panels"][aria-pressed="true"]'
    );
    const isContextSectionOpen =
      isNavHeaderContextActive ||
      !!document.querySelector('.cai-tool-panel.open') ||
      !!document.querySelector('.main-container.tool-panel-open');

    // Build ordered feature sequence based on current screen context.
    // skipValidation is implicit: the workspace handler always calls
    // startAllFeatureTours (isRestart=true) which bypasses both validateUser
    // and UpsertFeatureCompletion for the entire tour session.
    // On the create-agent screen we delegate directly to the Angular
    // CreateNewAgentComponent via a dedicated event so it can use
    // initializeCreateAgentTour (DOM-readiness retry) and then chain the
    // workspace-chat tour (without step 4). Neither ValidateUser nor
    // UpsertFeatureCompletion is called (skipValidation: true).
    // Stop any active React tour (e.g. profile-header) and close the settings
    // panel before launching a new tour sequence.  Without this the
    // profile-header tour can keep dispatching kci-tour-open-settings-panel
    // while the new tour overlay is already showing.
    onboardingTourService.stopTour();
    closeHeaderFlyin();

    if (isCreateAgentScreen) {
      window.dispatchEvent(
        new CustomEvent('kci-start-create-agent-tour', {
          detail: {
            skipValidation: true,
            followUpTour: {
              featureKey: 'workspace-chat',
              maxSteps: 5,
              excludeStepIds: ['context-based-prompt'],
            },
          },
        })
      );
      onStartWelcomeTutorial?.();
      return;
    }

    const featureSequence = isActivityDashboardScreen
      ? [
          { featureKey: 'activity' },
          {
            featureKey: 'workspace-chat',
            maxSteps: 5,
            excludeStepIds: ['context-based-prompt'],
          },
        ]
      : isContextSectionOpen
        ? [
            { featureKey: 'open-tools-tour' },
            {
              featureKey: 'workspace-chat',
              excludeStepIds: ['context-based-prompt'],
            },
          ]
        : [{ featureKey: 'workspace-chat' }];

    window.dispatchEvent(
      new CustomEvent('kci-start-workspace-tour', {
        detail: {
          skipValidation: true,
          featureSequence,
        },
      })
    );

    onStartWelcomeTutorial?.();
  };

  const resetSettingsState = () => {
    setLangExpanded(false);
    setLangSearchQuery('');
  };

  const closeHeaderFlyin = () => {
    resetSettingsState();
    setActiveFlyinMode(null);
  };

  const toggleNotificationsWithMutex = React.useCallback(() => {
    // Keep only one fly-in visible at a time.
    resetSettingsState();
    setActiveFlyinMode(null);
    onToggleNotifications();
  }, [onToggleNotifications]);

  const toggleHeaderFlyinWithMutex = React.useCallback(
    (mode: Exclude<HeaderFlyinMode, null>) => {
      onCloseNotifications();
      setActiveFlyinMode((currentMode) => {
        const nextMode = currentMode === mode ? null : mode;
        if (currentMode === 'settings' && nextMode !== 'settings') {
          resetSettingsState();
        }
        return nextMode;
      });
    },
    [onCloseNotifications],
  );

  // Direct event to open the settings fly-in from TourOverlay without going
  // through programmatic click (which can race with handleDocumentClick).
  useEffect(() => {
    const handleOpenSettingsForTour = () => {
      // The profile-header tour is the only flow that targets an element in
      // the settings fly-in. Ignore stale events after another tour starts or
      // after a tour closes so they cannot reopen the fly-in.
      const { isActive, currentFeature } = onboardingTourService.getSnapshot();
      if (!isActive || currentFeature !== 'profile-header') {
        return;
      }
      setActiveFlyinMode('settings');
    };
    window.addEventListener('kci-tour-open-settings-panel', handleOpenSettingsForTour);
    return () => window.removeEventListener('kci-tour-open-settings-panel', handleOpenSettingsForTour);
  }, [])

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const headerEl = headerRef.current;
      const menuEl = document.getElementById("userProfileMenu");
      const notificationMenuEl = document.getElementById("notificationMenu");

      const clickedInsideHeader = !!headerEl?.contains(target);
      const clickedInsideMenu = !!menuEl?.contains(target);
      const clickedInsideNotificationMenu =
        !!notificationMenuEl?.contains(target);
      const clickedInsideTourOverlay =
        target instanceof Element && !!target.closest('[data-tour-overlay]');

      if (clickedInsideHeader || clickedInsideMenu || clickedInsideNotificationMenu || clickedInsideTourOverlay) {
        return;
      }

      resetSettingsState();
      setActiveFlyinMode(null);
      onCloseNotifications();
    };

    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [onCloseNotifications]);

  const handleHeaderActionClick = (action: MainHeaderAction) => {
    switch (action.id) {
      case "notifications":
        toggleNotificationsWithMutex();
        return;

      case "feedback":
        closeHeaderFlyin();
        onOpenFeedback();
        return;

      case "settings": {
        // Only start the profile-header tour when the panel is being OPENED
        // (not when toggled closed). The tour targets the welcome-tutorial-btn
        // inside the settings fly-in, which is now in the DOM at this point.
        const isOpeningSettings = activeFlyinMode !== "settings";
        toggleHeaderFlyinWithMutex("settings");
        if (isOpeningSettings) {
          void onboardingTourService.startFeatureTour('profile-header');
        }
        return;
      }

      case "profile":
        toggleHeaderFlyinWithMutex("profile");
        return;

      default:
        return;
    }
  };

  const getFlyinTitle = () => {
    if (activeFlyinMode === "settings") {
      return settingsTitle;
    }

    return profileTitle;
  };

  const isHeaderFlyinAction = (actionId: MainHeaderActionId) => {
    return actionId === "profile" || actionId === "settings";
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`main-header${isCollapsed ? " main-header--collapsed" : ""}`}
      >
        {/* Left: KPMG brand + Clara intelligence */}
        <div className="main-header__left">
          <div className="main-header__left-inner">
            <svg
              className="main-header__brand-logo"
              viewBox="0 0 57 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="KPMG"
            >
              <path d={KPMG_LOGO_PATH} fill="white" />
            </svg>

            <div className="main-header__divider" />

            <div className="main-header__app-name">
              <span className="main-header__app-name-bold">{t('header.brandPrimary')}</span>
              <span className="main-header__app-name-regular">{t('header.brandSecondary')}</span>
            </div>
          </div>
        </div>

        {/* Center: Alert pill A (hidden by default) */}
        <div
          className={`main-header__alert${alertAVisible ? " main-header__alert--visible" : ""}`}
          id="headerAlert"
        >
          <span className="main-header__alert-icon material-symbols-outlined">
            vital_signs
          </span>
          <span className="main-header__alert-text">
            {t('header.alertA.text')}{" "}
            <strong>{viewWorkspaceLabel}</strong>
          </span>
          <button
            className="main-header__alert-close"
            type="button"
            aria-label={closeLabel}
            onClick={(e) => {
              e.stopPropagation();
              setAlertAVisible(false);
            }}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Center: Alert pill S (hidden by default) */}
        <div
          className={`main-header__alert${alertSVisible ? " main-header__alert--visible" : ""}`}
          id="headerAlertS"
        >
          <span className="main-header__alert-icon material-symbols-outlined">vital_signs</span>
          <span className="main-header__alert-text">
            {t('header.alertS.text')}{" "}
            <strong>{viewWorkspaceLabel}</strong>
          </span>
          <button
            className="main-header__alert-close"
            type="button"
            aria-label={closeLabel}
            onClick={(e) => {
              e.stopPropagation();
              setAlertSVisible(false);
            }}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Right: flag (hidden) + engagement (hidden) + divider (hidden) +
            notification bell + profile avatar */}
        <div className="main-header__right">
          <div className="main-header__right-inner">
            {/* Flag — hidden per prototype Session 34 */}
            <div
              className="main-header__flag"
              style={{ display: "none" }}
              aria-hidden="true"
            />

            {/* Engagement selector — hidden per prototype Session 34 */}
            <button
              className="main-header__engagement"
              type="button"
              style={{ display: "none" }}
              aria-hidden="true"
              tabIndex={-1}
            />

            {/* Right divider — hidden per prototype Session 34 */}
            <div
              className="main-header__right-divider"
              style={{ display: "none" }}
              aria-hidden="true"
            />
            <NotificationIcon
              unreadCount={unreadCount}
              isOpen={notifPanelOpen}
              onClick={toggleNotificationsWithMutex}
            />
            {/* Hidden to avoid a duplicate header notification icon while mock data is active. */}

            {/* Profile avatar */}
            <div className="main-header__actions">
              {actions
                .filter((action) => action.id !== "notifications")
                .map((action) => {
                const isProfileAction = action.id === "profile";
                const isFlyinAction = isHeaderFlyinAction(action.id);
                const actionTourId =
                  action.id === 'settings'
                    ? 'main-header-settings-btn'
                    : action.id === 'profile'
                      ? 'main-header-profile-btn'
                      : undefined;

                const isActive =
                  action.id === "notifications"
                    ? notifPanelOpen
                    : activeFlyinMode === action.id;

                return (
                  <button
                    key={action.id}
                    className={
                      isProfileAction
                        ? `main-header__profile${
                            isActive ? " main-header__profile--active" : ""
                          }`
                        : `main-header__action${
                            isActive ? " main-header__action--active" : ""
                          }`
                    }
                    type="button"
                    title={action.tooltip}
                    aria-label={action.label}
                    data-tour-id={actionTourId}
                    aria-pressed={isActive}
                    aria-expanded={isFlyinAction ? isActive : undefined}
                    aria-controls={isFlyinAction ? "userProfileMenu" : undefined}
                    onClick={() => handleHeaderActionClick(action)}
                  >
                    {isProfileAction ? (
                      <div className="main-header__avatar">
                        <Avatar
                          size={isCollapsed ? "sm" : "md"}
                          src={avatarUrl}
                          initials={userInitials}
                          alt={userName}
                        />
                      </div>
                    ) : (
                      <span className="material-symbols-outlined">
                        {action.icon}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* ── User Profile Menu ─────────────────────────────────────────── */}
      <div
        className={`user-profile-menu${headerFlyinOpen ? " user-profile-menu--open" : ""}`}
        id="userProfileMenu"
        role="dialog"
        aria-label={getFlyinTitle()}
        aria-hidden={!headerFlyinOpen}
      >
        {activeFlyinMode === "profile" && (
          <>
            <div className="user-profile-menu__header">
              <div>
                <div className="user-profile-menu__name">
                  {profileLoading && !account ? profileLoadingLabel : userName}
                </div>
                <div className="user-profile-menu__email">
                  {profileLoading && !account ? "" : userEmail}
                </div>
              </div>

              <button
                className="user-profile-menu__close"
                type="button"
                aria-label={closeLabel}
                onClick={closeHeaderFlyin}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="user-profile-menu__items">
              <button 
                className="user-profile-menu__item" 
                type="button"
                onClick={() => setDisclaimerOpen(true)}
                disabled={disclaimerLoading}
              >
                <span className="material-symbols-outlined user-profile-menu__icon">
                  description
                </span>
                <span className="user-profile-menu__label">
                  {disclaimerLabel}
                </span>
              </button>
              {disclaimerError && (
                <div className="user-profile-menu__error" role="alert">
                  {disclaimerError}
                </div>
              )}
            </div>
          </>
        )}

        {activeFlyinMode === "settings" && (
          <>
            <div className="user-profile-menu__header user-profile-menu__header--compact">
              <div className="user-profile-menu__title">{settingsTitle}</div>

              <button
                className="user-profile-menu__close"
                type="button"
                aria-label={closeLabel}
                onClick={closeHeaderFlyin}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="user-profile-menu__items">
              <button
                className="user-profile-menu__item"
                type="button"
                onClick={handleThemeToggle}
                disabled={prefsLoading}
              >
                <span className="material-symbols-outlined user-profile-menu__icon">dark_mode</span>
                <span className="user-profile-menu__label">
                  {currentTheme === 'light' ? darkModeLabel : lightModeLabel}
                </span>
              </button>

              {/* Language expand */}
              <button
                className={`user-profile-menu__item user-profile-menu__item--expandable${
                  langExpanded ? " user-profile-menu__item--expanded" : ""
                }`}
                type="button"
                aria-expanded={langExpanded}
                onClick={() => {
                  setLangExpanded((current) => {
                    const next = !current;
                    if (!next) {
                      setLangSearchQuery('');
                    }
                    return next;
                  });
                }}
              >
                <span className="material-symbols-outlined user-profile-menu__icon">language</span>
                <span className="user-profile-menu__label">
                  {languageLabel}: <span>{selectedLangName}</span>
                </span>
                <span
                  className={`material-symbols-outlined user-profile-menu__trailing user-profile-menu__chevron${
                    langExpanded ? " user-profile-menu__chevron--open" : ""
                  }`}
                >
                  chevron_right
                </span>
              </button>

              <div
                className={`user-profile-menu__lang-list${
                  langExpanded ? " user-profile-menu__lang-list--open" : ""
                }`}
                id="langList"
              >
                <div className="user-profile-menu__lang-search">
                  <span className="material-symbols-outlined user-profile-menu__lang-search-icon">
                    search
                  </span>
                  <input
                    type="text"
                    className="user-profile-menu__lang-search-input"
                    placeholder={searchLanguagePlaceholder}
                    value={langSearchQuery}
                    onChange={(event) => setLangSearchQuery(event.target.value)}
                    aria-label={searchLanguagePlaceholder}
                  />
                </div>

                {filteredLanguages.length === 0 ? (
                  <div className="user-profile-menu__lang-empty">{noLanguageResultsLabel}</div>
                ) : (
                  filteredLanguages.map((lang) => (
                    <label
                      key={lang.code}
                      className="user-profile-menu__lang-row"
                      data-lang={lang.code}
                    >
                      <span className="user-profile-menu__lang-name">
                        {getLanguageDisplayName(lang.code, lang.name)} ({lang.locale})
                      </span>
                      <input
                        type="checkbox"
                        className="context-menu__check"
                        checked={selectedLang === lang.locale || selectedLang === lang.code}
                        onChange={() => updateLanguage(lang.locale)}
                        disabled={prefsLoading}
                      />
                      <span className="context-menu__checkbox" />
                    </label>
                  ))
                )}
              </div>

              {/*
                TODO: Re-enable Memory Settings after backend contract and UX behavior are finalized.
                Previous implementation intentionally kept here for later activation.

                <button
                  className={`user-profile-menu__item user-profile-menu__item--expandable${
                    memoryExpanded ? " user-profile-menu__item--expanded" : ""
                  }`}
                  type="button"
                  aria-expanded={memoryExpanded}
                  onClick={() => setMemoryExpanded((current) => !current)}
                  disabled={prefsLoading}
                >
                  <span className="material-symbols-outlined user-profile-menu__icon">
                    psychology
                  </span>
                  <span className="user-profile-menu__label">
                    {memorySettingsLabel} {memoryEnabled ? `(${enabledLabel})` : `(${disabledLabel})`}
                  </span>
                  <span
                    className={`material-symbols-outlined user-profile-menu__trailing user-profile-menu__chevron${
                      memoryExpanded ? " user-profile-menu__chevron--open" : ""
                    }`}
                  >
                    chevron_right
                  </span>
                </button>

                <div
                  className={`user-profile-menu__memory-list${
                    memoryExpanded ? " user-profile-menu__memory-list--open" : ""
                  }`}
                  id="memoryList"
                >
                  <label className="user-profile-menu__memory-row">
                    <span className="user-profile-menu__memory-info">
                      <span className="user-profile-menu__memory-title">
                        {referenceSavedMemoriesLabel}
                      </span>
                      <span className="user-profile-menu__memory-desc">
                        {memorySavedDescriptionLabel}
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      className="context-menu__check"
                      checked={memoryEnabled}
                      onChange={(e) => updateMemory(e.target.checked)}
                      disabled={prefsLoading}
                    />
                    <span className="context-menu__checkbox" />
                  </label>

                  <label className="user-profile-menu__memory-row">
                    <span className="user-profile-menu__memory-info">
                      <span className="user-profile-menu__memory-title">
                        {referenceChatHistoryLabel}
                      </span>
                      <span className="user-profile-menu__memory-desc">
                        {memoryHistoryDescriptionLabel}
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      className="context-menu__check"
                      defaultChecked
                    />
                    <span className="context-menu__checkbox" />
                  </label>

                  <button
                    type="button"
                    className="user-profile-menu__memory-manage"
                  >
                    {manageSavedMemoriesLabel}
                  </button>
                </div>
              */}

              <button
                className="user-profile-menu__item"
                type="button"
                onClick={handleStartWelcomeTutorialClick}
              >
                <span className="material-symbols-outlined user-profile-menu__icon">
                  animated_images
                </span>
                <span className="user-profile-menu__label">
                  {welcomeTutorialLabel}
                </span>
              </button>

              <button
                className="user-profile-menu__item"
                data-tour-id="welcome-tutorial-btn"
                type="button"
                onClick={handleGuidesOpen}
                disabled={!guidesUrl} 
              >
                <span className="material-symbols-outlined user-profile-menu__icon">
                  auto_stories
                </span>
                <span className="user-profile-menu__label">{guidesLabel}</span>
                <span className="material-symbols-outlined user-profile-menu__trailing">
                  open_in_new
                </span>
              </button>

              <button
                className="user-profile-menu__item"
                type="button"
                onClick={handleSystemCardOpen}
                disabled={!systemCardUrl}
              >
                <span className="material-symbols-outlined user-profile-menu__icon">
                  sticky_note_2
                </span>
                <span className="user-profile-menu__label">
                  {systemCardLabel}
                </span>
                <span className="material-symbols-outlined user-profile-menu__trailing">
                  open_in_new
                </span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Disclaimer Modal Dialog */}
      {disclaimerContent && (
        <DialogBox
          open={disclaimerOpen}
          overlayClassName="main-header__disclaimer-overlay"
          className="main-header__disclaimer-dialog"
          title={disclaimerContent.title}
          size="md"
          variant="default"
          onClose={() => setDisclaimerOpen(false)}
          actions={[
            {
              label: isAcknowledging ? `${acceptLabel}...` : acceptLabel,
              variant: "primary",
              onClick: async () => {
                try {
                  await acknowledgeDisclaimer();
                  await resetTokenTimeout();
                  setDisclaimerOpen(false);
                } catch {
                  // Error already handled in hook
                }
              },
              disabled: isAcknowledging,
            },
          ]}
        >
          {renderDisclaimerContent(disclaimerContent.content)}
        </DialogBox>
      )}
    </>
  );
});

export default MainHeader;